"use client";

import { routeReadyTelemetryEventName } from "@/components/shell/RouteReadyTelemetry";
import type { RouteOwnedReadinessSignal } from "@/components/shell/RouteReadyTelemetry";
import { normalizeClientRouteFamily } from "@/utils/client-route-family";
import { createPageLoadMeasurementId, getHardNavigationReadyDurationMs, getPageLoadAutomationSignal } from "@/utils/page-load-telemetry";
import type { PageLoadAutomationSignal, PageLoadMeasurementKind, PageLoadReadinessSignal } from "@/utils/page-load-telemetry";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

type PageLoadTelemetryPayload = {
  automationSignal: PageLoadAutomationSignal;
  durationMs: number;
  measurementId: string;
  measurementKind: PageLoadMeasurementKind;
  navigationType: "back_forward" | "navigate" | "prerender" | "reload" | "soft" | "unknown";
  readinessSignal: PageLoadReadinessSignal;
  routeFamily: string;
  serverTraceId?: string;
};

type PendingNavigationStart = {
  pathname: string;
  startedAt: number;
};

type ActiveNavigationMeasurement = {
  measurementKind: PageLoadMeasurementKind;
  navigationType: PageLoadTelemetryPayload["navigationType"];
  pathname: string;
  routeFamily: string;
  serverTraceId?: string;
  startedAt: number;
};

const telemetryEndpoint = "/api/telemetry/page-load";
const maxDurationMs = 120_000;
const pendingNavigationStartTtlMs = 30_000;
const maxRememberedRouteReadinessMeasurements = 200;

function isInternalAnchor(anchor: HTMLAnchorElement): boolean {
  if (!anchor.href || anchor.target || anchor.hasAttribute("download")) {
    return false;
  }

  const destination = new URL(anchor.href, window.location.href);
  return destination.origin === window.location.origin
    && destination.pathname !== window.location.pathname;
}

function getInternalAnchorPathname(anchor: HTMLAnchorElement): string | null {
  if (!isInternalAnchor(anchor)) {
    return null;
  }

  return new URL(anchor.href, window.location.href).pathname;
}

function sendPageLoadTelemetry(payload: PageLoadTelemetryPayload) {
  const body = JSON.stringify(payload);

  if (navigator.sendBeacon) {
    const sent = navigator.sendBeacon(
      telemetryEndpoint,
      new Blob([body], { type: "application/json" }),
    );
    if (sent) {
      return;
    }
  }

  void fetch(telemetryEndpoint, {
    body,
    headers: { "content-type": "application/json" },
    keepalive: true,
    method: "POST",
  }).catch(() => undefined);
}

function reportRouteLoad(payload: PageLoadTelemetryPayload) {
  if (
    !Number.isFinite(payload.durationMs)
    || payload.durationMs < 0
    || payload.durationMs > maxDurationMs
  ) {
    return;
  }

  sendPageLoadTelemetry({
    ...payload,
    durationMs: Math.round(payload.durationMs),
  });
}

function getNavigationEntry(): PerformanceNavigationTiming | null {
  const entry = performance.getEntriesByType("navigation")[0];
  return entry?.entryType === "navigation" ? entry as PerformanceNavigationTiming : null;
}

function getHardNavigationType(entry: PerformanceNavigationTiming): PageLoadTelemetryPayload["navigationType"] {
  switch (entry.type) {
    case "back_forward":
    case "navigate":
    case "prerender":
    case "reload": {
      return entry.type;
    }
    default: {
      return "unknown";
    }
  }
}

export default function PageLoadTelemetryReporter({ serverTraceId }: { serverTraceId?: string }) {
  const pathname = usePathname();
  const pendingNavigationStartRef = useRef<PendingNavigationStart | null>(null);
  const lastReportedPathnameRef = useRef<string | null>(null);
  const activeNavigationMeasurementRef = useRef<ActiveNavigationMeasurement | null>(null);
  const reportedRouteReadinessRef = useRef(new Set<string>());

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target instanceof Element ? event.target.closest("a") : null;
      if (target instanceof HTMLAnchorElement) {
        const destinationPathname = getInternalAnchorPathname(target);
        if (destinationPathname) {
          pendingNavigationStartRef.current = {
            pathname: destinationPathname,
            startedAt: performance.now(),
          };
        }
      }
    };

    document.addEventListener("click", handleClick, { capture: true });

    return () => {
      document.removeEventListener("click", handleClick, { capture: true });
    };
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      return;
    }

    const handleRouteReady = (event: Event) => {
      const measurement = activeNavigationMeasurementRef.current;
      const signal = event instanceof CustomEvent
          && typeof event.detail === "object"
          && event.detail !== null
          && "signal" in event.detail
        ? (event.detail as { signal?: RouteOwnedReadinessSignal }).signal
        : undefined;

      if (!measurement || !signal) {
        return;
      }

      const deduplicationKey = `${measurement.pathname}:${measurement.startedAt}:${signal}`;
      if (reportedRouteReadinessRef.current.has(deduplicationKey)) {
        return;
      }
      reportedRouteReadinessRef.current.add(deduplicationKey);
      if (reportedRouteReadinessRef.current.size > maxRememberedRouteReadinessMeasurements) {
        const oldestKey = reportedRouteReadinessRef.current.values().next().value;
        if (oldestKey) {
          reportedRouteReadinessRef.current.delete(oldestKey);
        }
      }

      reportRouteLoad({
        automationSignal: getPageLoadAutomationSignal(navigator.webdriver),
        durationMs: performance.now() - measurement.startedAt,
        measurementId: createPageLoadMeasurementId(crypto),
        measurementKind: measurement.measurementKind,
        navigationType: measurement.navigationType,
        readinessSignal: signal,
        routeFamily: measurement.routeFamily,
        ...(measurement.serverTraceId ? { serverTraceId: measurement.serverTraceId } : {}),
      });
    };

    window.addEventListener(routeReadyTelemetryEventName, handleRouteReady);
    return () => window.removeEventListener(routeReadyTelemetryEventName, handleRouteReady);
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production" || !pathname) {
      return;
    }

    const routeFamily = normalizeClientRouteFamily(pathname);
    const lastReportedPathname = lastReportedPathnameRef.current;

    if (!lastReportedPathname) {
      lastReportedPathnameRef.current = pathname;
      const entry = getNavigationEntry();
      activeNavigationMeasurementRef.current = {
        measurementKind: "page_load",
        navigationType: entry ? getHardNavigationType(entry) : "unknown",
        pathname,
        routeFamily,
        ...(serverTraceId ? { serverTraceId } : {}),
        startedAt: entry?.startTime ?? 0,
      };
      let secondFrame: number | null = null;
      let canceled = false;
      const reportHardNavigation = () => {
        const firstFrame = requestAnimationFrame(() => {
          secondFrame = requestAnimationFrame(() => {
            if (canceled) {
              return;
            }

            const entry = getNavigationEntry();
            reportRouteLoad({
              automationSignal: getPageLoadAutomationSignal(navigator.webdriver),
              durationMs: getHardNavigationReadyDurationMs(entry, performance.now()),
              measurementId: createPageLoadMeasurementId(crypto),
              measurementKind: "page_load",
              navigationType: entry ? getHardNavigationType(entry) : "unknown",
              readinessSignal: "document_ready",
              routeFamily,
              ...(serverTraceId ? { serverTraceId } : {}),
            });
          });
        });

        return () => {
          canceled = true;
          cancelAnimationFrame(firstFrame);
          if (secondFrame !== null) {
            cancelAnimationFrame(secondFrame);
          }
        };
      };

      if (document.readyState !== "loading") {
        return reportHardNavigation();
      } else {
        let cancelFrames: (() => void) | null = null;
        const handleDocumentReady = () => {
          cancelFrames = reportHardNavigation();
        };
        document.addEventListener("DOMContentLoaded", handleDocumentReady, { once: true });
        return () => {
          canceled = true;
          document.removeEventListener("DOMContentLoaded", handleDocumentReady);
          cancelFrames?.();
        };
      }

      return;
    }

    if (lastReportedPathname === pathname) {
      return;
    }

    const pendingNavigationStart = pendingNavigationStartRef.current;
    const now = performance.now();
    const startedAt = pendingNavigationStart
        && pendingNavigationStart.pathname === pathname
        && now - pendingNavigationStart.startedAt <= pendingNavigationStartTtlMs
      ? pendingNavigationStart.startedAt
      : now;
    pendingNavigationStartRef.current = null;
    lastReportedPathnameRef.current = pathname;
    activeNavigationMeasurementRef.current = {
      measurementKind: "route_transition",
      navigationType: "soft",
      pathname,
      routeFamily,
      startedAt,
    };

    let secondFrame: number | null = null;
    let canceled = false;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        if (canceled) {
          return;
        }
        reportRouteLoad({
          automationSignal: getPageLoadAutomationSignal(navigator.webdriver),
          durationMs: performance.now() - startedAt,
          measurementId: createPageLoadMeasurementId(crypto),
          measurementKind: "route_transition",
          navigationType: "soft",
          readinessSignal: "route_committed",
          routeFamily,
        });
      });
    });

    return () => {
      canceled = true;
      cancelAnimationFrame(firstFrame);
      if (secondFrame !== null) {
        cancelAnimationFrame(secondFrame);
      }
    };
  }, [pathname, serverTraceId]);

  return null;
}
